/**
 * ReliabilityAdminContent - Admin panel for managing reliability data sources
 * 
 * This is a compliance-critical panel for managing:
 * - Reliability data sources (MIL-HDBK-217, vendor data, industry databases)
 * - Failure rate records with citations
 * - Approval workflows for data sources
 * 
 * CERTIFICATION: All failure rates used in safety analyses trace back to
 * approved, versioned source documents via this interface.
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui';
import { 
  reliabilityApi, 
  type ReliabilityDataSource, 
  type FailureRateRecord 
} from '../../services/api';
import { logger } from '../../utils/logger';

type ReliabilitySourceType = ReliabilityDataSource['sourceType'];
type DataSourceApprovalStatus = ReliabilityDataSource['approvalStatus'];

type ActiveTab = 'sources' | 'failure-rates' | 'pending-approval';

interface ReliabilityAdminContentProps {
  loading: boolean;
  isSearching: boolean;
  searchQuery: string;
  onSourceClick?: (source: ReliabilityDataSource) => void;
  onRecordClick?: (record: FailureRateRecord) => void;
}

export function ReliabilityAdminContent({
  loading,
  isSearching,
  searchQuery,
  onSourceClick,
  onRecordClick,
}: ReliabilityAdminContentProps) {
  const [activeTab, setActiveTab] = useState<ActiveTab>('sources');
  const [sources, setSources] = useState<ReliabilityDataSource[]>([]);
  const [records, setRecords] = useState<FailureRateRecord[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Load data on mount
  useEffect(() => {
    loadReliabilityData();
  }, []);
  
  const loadReliabilityData = async () => {
    setLoadingData(true);
    setError(null);
    try {
      // Try to load from API, fall back to placeholder data if API not available
      try {
        const [sourcesRes, recordsRes] = await Promise.all([
          reliabilityApi.listSources(),
          reliabilityApi.listRecords(),
        ]);
        setSources(sourcesRes);
        setRecords(recordsRes);
        setPendingCount(sourcesRes.filter(s => s.approvalStatus === 'pending').length);
      } catch (apiErr) {
        // API not available yet, use placeholder data for development
        logger.warn('Reliability API not available, using placeholder data:', apiErr);
        
        setSources([
          {
            sourceId: 'MIL-217F-N2',
            sourceType: 'mil_std',
            sourceName: 'MIL-HDBK-217F Notice 2',
            version: 'Notice 2',
            publicationDate: '1995-02-28',
            retrievalDate: '2024-06-15',
            approvalStatus: 'approved',
            approvedBy: 'John Smith',
            approvalDate: '2024-06-20',
            documentReference: 's3://devproc-reference-docs/mil-hdbk-217f.pdf',
            notes: 'Primary source for electronic component failure rates',
            createdAt: '2024-06-15T10:00:00Z',
            createdBy: 'admin@aerotage.com',
          },
          {
            sourceId: 'NPRD-2016',
            sourceType: 'industry_db',
            sourceName: 'NPRD-2016 (Nonelectronic Parts Reliability Data)',
            version: '2016',
            publicationDate: '2016-01-01',
            retrievalDate: '2024-07-01',
            approvalStatus: 'approved',
            approvedBy: 'Jane Doe',
            approvalDate: '2024-07-05',
            notes: 'For mechanical and electromechanical components',
            createdAt: '2024-07-01T10:00:00Z',
            createdBy: 'admin@aerotage.com',
          },
          {
            sourceId: 'VENDOR-COLLINS-001',
            sourceType: 'vendor',
            sourceName: 'Collins Aerospace VHF-4000 FMEDA Report',
            version: 'Rev C',
            publicationDate: '2023-11-15',
            retrievalDate: '2024-08-10',
            approvalStatus: 'pending',
            documentReference: 's3://devproc-vendor-docs/collins-vhf4000-fmeda.pdf',
            notes: 'Product-specific FMEDA for VHF-4000 transceiver',
            createdAt: '2024-08-10T14:30:00Z',
            createdBy: 'engineer@aerotage.com',
          },
        ]);
        
        setRecords([
          {
            recordId: 'FR-MIL217-IC-001',
            sourceId: 'MIL-217F-N2',
            partType: 'IC, Linear, Operational Amplifier',
            partCategory: 'Integrated Circuits',
            failureRate: 0.0038e-6,
            failureRateUnit: 'per_hour',
            environmentalCategory: 'AIC',
            qualityLevel: 'MIL-SPEC',
            citation: {
              sourceDocument: 'MIL-HDBK-217F Notice 2',
              section: 'Section 5.1',
              table: 'Table 5.1.2-1',
              pageNumber: '5-12',
              accessDate: '2024-06-15',
              verifiedBy: 'John Smith',
              verificationDate: '2024-06-20',
            },
            createdAt: '2024-06-15T10:30:00Z',
            createdBy: 'admin@aerotage.com',
          },
          {
            recordId: 'FR-MIL217-RES-001',
            sourceId: 'MIL-217F-N2',
            partType: 'Resistor, Film, Fixed',
            partCategory: 'Resistors',
            failureRate: 0.00023e-6,
            failureRateUnit: 'per_hour',
            environmentalCategory: 'AIC',
            qualityLevel: 'MIL-SPEC',
            temperatureRange: { min: -55, max: 125 },
            failureModes: [
              { mode: 'Open', percentage: 60 },
              { mode: 'Drift (>10%)', percentage: 30 },
              { mode: 'Short', percentage: 10 },
            ],
            citation: {
              sourceDocument: 'MIL-HDBK-217F Notice 2',
              section: 'Section 9.1',
              table: 'Table 9.1-1',
              accessDate: '2024-06-15',
              verifiedBy: 'John Smith',
              verificationDate: '2024-06-20',
            },
            createdAt: '2024-06-15T10:35:00Z',
            createdBy: 'admin@aerotage.com',
          },
        ]);
        
        setPendingCount(1);
      }
    } catch (err) {
      setError(`Failed to load reliability data: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoadingData(false);
    }
  };
  
  // Filter data based on search query
  const filterSources = (sourcesList: ReliabilityDataSource[]) => {
    if (!searchQuery.trim()) return sourcesList;
    const query = searchQuery.toLowerCase();
    return sourcesList.filter(s =>
      s.sourceName.toLowerCase().includes(query) ||
      s.sourceId.toLowerCase().includes(query) ||
      s.sourceType.toLowerCase().includes(query)
    );
  };
  
  const filterRecords = (recordsList: FailureRateRecord[]) => {
    if (!searchQuery.trim()) return recordsList;
    const query = searchQuery.toLowerCase();
    return recordsList.filter(r =>
      r.partType.toLowerCase().includes(query) ||
      r.recordId.toLowerCase().includes(query) ||
      r.partNumber?.toLowerCase().includes(query) ||
      r.partCategory?.toLowerCase().includes(query)
    );
  };
  
  const filteredSources = filterSources(sources);
  const filteredRecords = filterRecords(records);
  const pendingSources = sources.filter(s => s.approvalStatus === 'pending');
  
  // Loading state
  if (loading || loadingData) {
    return (
      <div className="p-8 text-center">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3 mx-auto"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-muted rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  // Searching state
  if (isSearching) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin text-3xl mb-3">🔍</div>
        <p className="text-muted-foreground">Searching reliability data...</p>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="p-4 m-4 bg-destructive-light border border-destructive rounded-lg">
        <p className="text-destructive">{error}</p>
        <Button variant="secondary" onClick={loadReliabilityData} className="mt-3">
          Retry
        </Button>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col h-full">
      {/* Summary stats */}
      <div className="flex-shrink-0 p-4 grid grid-cols-3 gap-4">
        <StatCard
          label="Data Sources"
          value={sources.length}
          subtext={`${sources.filter(s => s.approvalStatus === 'approved').length} approved`}
          icon="📚"
        />
        <StatCard
          label="Failure Rates"
          value={records.length}
          subtext="records indexed"
          icon="📊"
        />
        <StatCard
          label="Pending Approval"
          value={pendingCount}
          subtext="sources need review"
          icon="⏳"
          highlight={pendingCount > 0}
        />
      </div>
      
      {/* Tabs */}
      <div className="flex-shrink-0 px-4 border-b border-border">
        <div className="flex gap-4">
          <TabButton
            active={activeTab === 'sources'}
            onClick={() => setActiveTab('sources')}
            label="Data Sources"
            count={filteredSources.length}
          />
          <TabButton
            active={activeTab === 'failure-rates'}
            onClick={() => setActiveTab('failure-rates')}
            label="Failure Rates"
            count={filteredRecords.length}
          />
          <TabButton
            active={activeTab === 'pending-approval'}
            onClick={() => setActiveTab('pending-approval')}
            label="Pending Approval"
            count={pendingSources.length}
            highlight={pendingSources.length > 0}
          />
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'sources' && (
          <SourcesList
            sources={filteredSources}
            onSourceClick={onSourceClick}
            searchQuery={searchQuery}
          />
        )}
        {activeTab === 'failure-rates' && (
          <RecordsList
            records={filteredRecords}
            sources={sources}
            onRecordClick={onRecordClick}
            searchQuery={searchQuery}
          />
        )}
        {activeTab === 'pending-approval' && (
          <PendingApprovalList
            sources={pendingSources}
            onApprove={async (sourceId) => {
              try {
                await reliabilityApi.approveSource(sourceId);
              } catch (err) {
                logger.warn('Approve API not available:', err);
              }
              await loadReliabilityData();
            }}
            onReject={async (sourceId) => {
              try {
                await reliabilityApi.deprecateSource(sourceId);
              } catch (err) {
                logger.warn('Deprecate API not available:', err);
              }
              await loadReliabilityData();
            }}
          />
        )}
      </div>
    </div>
  );
}

// Stat card component
interface StatCardProps {
  label: string;
  value: number;
  subtext: string;
  icon: string;
  highlight?: boolean;
}

function StatCard({ label, value, subtext, icon, highlight }: StatCardProps) {
  return (
    <div className={`p-4 rounded-lg border ${highlight ? 'border-warning bg-warning-light' : 'border-border bg-muted/30'}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-lg">{icon}</span>
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
      </div>
      <div className="text-2xl font-bold text-foreground">{value}</div>
      <div className="text-xs text-muted-foreground">{subtext}</div>
    </div>
  );
}

// Tab button component
interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  highlight?: boolean;
}

function TabButton({ active, onClick, label, count, highlight }: TabButtonProps) {
  return (
    <Button
      onClick={onClick}
      variant="ghost"
      className={`py-2 px-1 text-sm font-medium border-b-2 rounded-none h-auto ${
        active
          ? 'border-primary text-info'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {label}
      <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${
        highlight 
          ? 'bg-warning text-warning-foreground' 
          : 'bg-muted text-muted-foreground'
      }`}>
        {count}
      </span>
    </Button>
  );
}

// Sources list component
interface SourcesListProps {
  sources: ReliabilityDataSource[];
  onSourceClick?: (source: ReliabilityDataSource) => void;
  searchQuery: string;
}

function SourcesList({ sources, onSourceClick, searchQuery }: SourcesListProps) {
  if (sources.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="text-5xl mb-4">📚</div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {searchQuery ? 'No matching data sources' : 'No data sources yet'}
        </h3>
        <p className="text-muted-foreground">
          {searchQuery
            ? `No sources match "${searchQuery}"`
            : 'Add reliability data sources like MIL-HDBK-217 or vendor FMEDA reports'}
        </p>
      </div>
    );
  }
  
  // Group by source type
  const groupedSources = sources.reduce((acc, source) => {
    const type = source.sourceType;
    if (!acc[type]) acc[type] = [];
    acc[type].push(source);
    return acc;
  }, {} as Record<ReliabilitySourceType, ReliabilityDataSource[]>);
  
  const sourceTypeLabels: Record<ReliabilitySourceType, { label: string; icon: string }> = {
    mil_std: { label: 'Military Standards', icon: '🎖️' },
    vendor: { label: 'Vendor Data', icon: '🏭' },
    industry_db: { label: 'Industry Databases', icon: '📊' },
    company: { label: 'Company Data', icon: '🏢' },
    derived: { label: 'Derived/Calculated', icon: '🔢' },
  };
  
  return (
    <div className="space-y-4">
      {Object.entries(groupedSources).map(([type, typeSources]) => (
        <div key={type} className="border border-border rounded-lg overflow-hidden">
          <div className="px-4 py-2 bg-muted/30 flex items-center gap-2">
            <span>{sourceTypeLabels[type as ReliabilitySourceType]?.icon}</span>
            <span className="font-medium text-foreground">
              {sourceTypeLabels[type as ReliabilitySourceType]?.label}
            </span>
            <span className="text-sm text-muted-foreground">({typeSources.length})</span>
          </div>
          <div className="divide-y divide-border">
            {typeSources.map(source => (
              <SourceCard
                key={source.sourceId}
                source={source}
                onClick={() => onSourceClick?.(source)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Source card component
interface SourceCardProps {
  source: ReliabilityDataSource;
  onClick: () => void;
}

function SourceCard({ source, onClick }: SourceCardProps) {
  const getStatusBadge = (status: DataSourceApprovalStatus) => {
    switch (status) {
      case 'approved':
        return 'bg-success-light text-success';
      case 'pending':
        return 'bg-warning-light text-warning';
      case 'deprecated':
        return 'bg-muted text-muted-foreground';
    }
  };
  
  return (
    <div
      className="p-4 hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-muted-foreground">
              {source.sourceId}
            </span>
            <span className="text-xs text-muted-foreground">
              v{source.version}
            </span>
          </div>
          <h4 className="font-medium text-foreground mt-0.5">
            {source.sourceName}
          </h4>
        </div>
        
        <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full ${getStatusBadge(source.approvalStatus)}`}>
          {source.approvalStatus}
        </span>
      </div>
      
      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
        <span>Published: {source.publicationDate}</span>
        {source.approvedBy && (
          <span>Approved by: {source.approvedBy}</span>
        )}
      </div>
      
      {source.notes && (
        <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
          {source.notes}
        </p>
      )}
    </div>
  );
}

// Records list component
interface RecordsListProps {
  records: FailureRateRecord[];
  sources: ReliabilityDataSource[];
  onRecordClick?: (record: FailureRateRecord) => void;
  searchQuery: string;
}

function RecordsList({ records, sources, onRecordClick, searchQuery }: RecordsListProps) {
  const getSourceName = (sourceId: string) => {
    const source = sources.find(s => s.sourceId === sourceId);
    return source?.sourceName || sourceId;
  };
  
  if (records.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="text-5xl mb-4">📊</div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {searchQuery ? 'No matching failure rates' : 'No failure rate records'}
        </h3>
        <p className="text-muted-foreground">
          {searchQuery
            ? `No records match "${searchQuery}"`
            : 'Import failure rate data from approved sources'}
        </p>
      </div>
    );
  }
  
  // Group by part category
  const groupedRecords = records.reduce((acc, record) => {
    const category = record.partCategory || 'Uncategorized';
    if (!acc[category]) acc[category] = [];
    acc[category].push(record);
    return acc;
  }, {} as Record<string, FailureRateRecord[]>);
  
  return (
    <div className="space-y-4">
      {Object.entries(groupedRecords).map(([category, categoryRecords]) => (
        <div key={category} className="border border-border rounded-lg overflow-hidden">
          <div className="px-4 py-2 bg-muted/30 flex items-center gap-2">
            <span className="font-medium text-foreground">{category}</span>
            <span className="text-sm text-muted-foreground">({categoryRecords.length})</span>
          </div>
          <div className="divide-y divide-border">
            {categoryRecords.map(record => (
              <RecordCard
                key={record.recordId}
                record={record}
                sourceName={getSourceName(record.sourceId)}
                onClick={() => onRecordClick?.(record)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Record card component
interface RecordCardProps {
  record: FailureRateRecord;
  sourceName: string;
  onClick: () => void;
}

function RecordCard({ record, sourceName, onClick }: RecordCardProps) {
  // Format failure rate for display
  const formatRate = (rate: number, unit: string) => {
    const formatted = rate.toExponential(2);
    switch (unit) {
      case 'per_hour':
        return `${formatted}/hr`;
      case 'per_million_hours':
        return `${formatted}/10⁶ hr`;
      case 'FIT':
        return `${formatted} FIT`;
      default:
        return formatted;
    }
  };
  
  return (
    <div
      className="p-4 hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-muted-foreground">
              {record.recordId}
            </span>
          </div>
          <h4 className="font-medium text-foreground mt-0.5">
            {record.partType}
          </h4>
          {record.partNumber && (
            <span className="text-xs text-muted-foreground">
              P/N: {record.partNumber}
            </span>
          )}
        </div>
        
        <div className="text-right flex-shrink-0">
          <div className="font-mono text-sm font-medium text-foreground">
            λ = {formatRate(record.failureRate, record.failureRateUnit)}
          </div>
          {record.environmentalCategory && (
            <span className="text-xs text-muted-foreground">
              Env: {record.environmentalCategory}
            </span>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
        <span>📚 {sourceName}</span>
        {record.citation.table && <span>📋 {record.citation.table}</span>}
        {record.citation.verifiedBy && (
          <span className="text-success">✓ Verified</span>
        )}
      </div>
      
      {record.failureModes && record.failureModes.length > 0 && (
        <div className="flex gap-2 mt-2">
          {record.failureModes.map((mode, idx) => (
            <span
              key={idx}
              className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground"
            >
              {mode.mode}: {mode.percentage}%
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Pending approval list component
interface PendingApprovalListProps {
  sources: ReliabilityDataSource[];
  onApprove: (sourceId: string) => Promise<void>;
  onReject: (sourceId: string) => Promise<void>;
}

function PendingApprovalList({ sources, onApprove, onReject }: PendingApprovalListProps) {
  const [processingId, setProcessingId] = useState<string | null>(null);
  
  if (sources.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="text-5xl mb-4">✅</div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          No pending approvals
        </h3>
        <p className="text-muted-foreground">
          All reliability data sources have been reviewed
        </p>
      </div>
    );
  }
  
  const handleApprove = async (sourceId: string) => {
    setProcessingId(sourceId);
    try {
      await onApprove(sourceId);
    } finally {
      setProcessingId(null);
    }
  };
  
  const handleReject = async (sourceId: string) => {
    setProcessingId(sourceId);
    try {
      await onReject(sourceId);
    } finally {
      setProcessingId(null);
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="p-4 bg-warning-light border border-warning rounded-lg">
        <p className="text-sm text-warning-foreground">
          <strong>⚠️ Review Required:</strong> The following data sources need approval before 
          their failure rates can be used in safety analyses.
        </p>
      </div>
      
      {sources.map(source => (
        <div key={source.sourceId} className="border border-border rounded-lg p-4">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <div className="text-xs font-mono text-muted-foreground">
                {source.sourceId}
              </div>
              <h4 className="font-medium text-foreground">
                {source.sourceName}
              </h4>
              <div className="text-xs text-muted-foreground mt-1">
                Type: {source.sourceType} | Version: {source.version} | Published: {source.publicationDate}
              </div>
            </div>
          </div>
          
          {source.notes && (
            <p className="text-sm text-muted-foreground mb-3">
              {source.notes}
            </p>
          )}
          
          <div className="text-xs text-muted-foreground mb-3">
            Submitted by: {source.createdBy} on {new Date(source.createdAt).toLocaleDateString()}
          </div>
          
          {source.documentReference && (
            <div className="text-xs mb-3">
              <a 
                href={source.documentReference} 
                className="text-info hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                📎 View Source Document
              </a>
            </div>
          )}
          
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => handleApprove(source.sourceId)}
              disabled={processingId !== null}
            >
              {processingId === source.sourceId ? 'Processing...' : '✓ Approve'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleReject(source.sourceId)}
              disabled={processingId !== null}
            >
              ✗ Reject
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
